import cache from "../shared/cache";

/**
 * Class representing a draggable and interactive menu component.
 */
export default class Menu {
    /** @type {Object} Handler object controlling the open/close state of the menu */
    #handler;
    
    /** @type {HTMLElement} The inner menu element */
    #innerMenu;

    /** @type {boolean} Flag indicating if dragging is in progress */
    #isDragging = false;

    /** @type {number|null} Timeout ID for holding to initiate drag */
    #holdingId = null;

    /** @type {boolean} Flag indicating if the menu is currently movable */
    #isMovable = false;

    /** @type {number} The initial x position during drag start */
    #sx;

    /** @type {number} The initial y position during drag start */
    #sy;

    /** @type {number} The current x position of the menu */
    #x;

    /** @type {number} The current y position of the menu */
    #y;

    /** @type {Function|null} Callback function triggered on menu click */
    #onClick;

    /** @type {Function|null} Callback function triggered while dragging */
    #onMove;

    /** @type {string} Store the original background image for restoring when menu closes */
    #originalBackgroundImage;

    /**
     * Create a menu instance.
     * @param {Object} options - The configuration object.
     * @param {string} [options.id='adv-menu'] - The ID to assign to the menu element.
     * @param {string} options.innerMenu - The HTML content of the inner menu.
     * @param {string} options.className - The class to assign to the menu container.
     * @param {string} options.img - The CSS background-image value.
     * @param {Function|null} [options.onClick=null] - Callback when menu is clicked.
     * @param {Function|null} [options.onMove=null] - Callback during menu drag.
     */
    constructor({id = 'adv-menu', innerMenu = null, className, img, onClick = null, onMove = null}) {
        this.menu = document.createElement('div');
        this.storedPosition = cache.get('os-menu-position') || {
            x: 90,
            y: 90
        };
        this.#init(id, innerMenu, className, img);
        this.#onClick = onClick;
        this.#onMove = onMove;
    }

    /**
     * Get the percentual position based on x and y coordinates.
     * @param {number} x  
     * @param {number} y 
     * @returns {{ x: number, y: number }} Percentual position.
     */
    #getPercentualPosition = (x, y) => {
        const clamp = (n1, n2) => {
            return Math.max(Math.min(n1, n2), 1);
        }
        const xPercent = clamp(((x / window.innerWidth) * 100), 97.5);
        const yPercent = clamp(((y / window.innerHeight) * 100), 97.5);

        return {
            x: xPercent,
            y: yPercent
        };
    }

    /**
     * Updates visual indicator when dragging.
     * @private
     */
    #draggingVisual = () => {
        let draggingClass = 'scf-adv-menu-popup--dragging';
        if (this.#isMovable) {
            this.menu.classList.add(draggingClass);
        } else {
            this.menu.classList.remove(draggingClass);
        }
    }

    /**
     * Hide the menu icon.
     * @public
     */
    hideIcon = () => {
        this.menu.style.backgroundImage = 'none';
    }

    /**
     * Show the menu icon.
     * @public
     */
    showIcon = () => {
        this.menu.style.backgroundImage = this.#originalBackgroundImage;
    }

    /**
     * Get mouse or touch position.
     * @param {MouseEvent|TouchEvent} e - The event object.
     * @returns {{x: number, y: number}} Coordinates.
     * @private
     */
    #getMousePos = (e) => ({
        x: e.touches ? e.touches[0].clientX : e.clientX,
        y: e.touches ? e.touches[0].clientY : e.clientY
    })  

    /**
     * Handler for drag start.
     * @param {MouseEvent|TouchEvent} e - The event object.
     * @private
     */
    #onDragStart = (e) => {
        e.preventDefault();
        this.#isDragging = true;

        clearTimeout(this.#holdingId);

        this.#holdingId = setTimeout(() => {
            if (this.#isDragging) {
                this.#isMovable = true;
                this.#draggingVisual();
            }
        }, 500);

        const pos = this.#getMousePos(e);
        this.#x = this.#sx = pos.x;
        this.#y = this.#sy = pos.y;

        document.addEventListener('mousemove', this.#onDrag);
        document.addEventListener('mouseup', this.#onDragEnd);
        document.addEventListener('touchmove', this.#onDrag, { passive: false });
        document.addEventListener('touchend', this.#onDragEnd);
    };

    /**
     * Handler for drag move.
     * @param {MouseEvent|TouchEvent} e - The event object.
     * @private
     */
    #onDrag = (e) => {
        if (!this.#isMovable) return;

        const pos = this.#getMousePos(e);
        this.#x = pos.x;
        this.#y = pos.y;

        this.setPosition(this.#getPercentualPosition(this.#x, this.#y));
    };

    /**
     * Handler for drag end.
     * @param {MouseEvent|TouchEvent} e - The event object.
     * @private
     */
    #onDragEnd = (e) => {
        this.#isDragging = false;
        this.#isMovable = false;

        this.storedPosition.x = this.#x;
        this.storedPosition.y = this.#y;

        document.removeEventListener('mousemove', this.#onDrag);
        document.removeEventListener('mouseup', this.#onDragEnd);
        document.removeEventListener('touchmove', this.#onDrag);
        document.removeEventListener('touchend', this.#onDragEnd);
        
        if (this.#sx !== this.#x || this.#sy !== this.#y) {
            cache.set('os-menu-position', this.#getPercentualPosition(this.storedPosition.x, this.storedPosition.y));
        } else if (this.#onClick) {
            this.#onClick({handler: this.#handler});
        }
    };

    /**
     * Initialize the menu.
     * @param {string} id - The menu element ID.
     * @param {string?} innerMenu - Inner HTML content of the menu.
     * @param {string} className - CSS class name.
     * @param {string} img - CSS background image string.
     * @private
     */
    #init = (id, innerMenu, className, img) => {
        this.menu.className = className;
        this.menu.style.backgroundImage = img;
        this.#originalBackgroundImage = img;
        this.menu.id = id;
        this.menu.innerHTML = innerMenu;
        this.#innerMenu = this.menu.firstElementChild;
        this.#x = this.#sx = this.storedPosition.x;
        this.#y = this.#sy = this.storedPosition.y;
        document.body.append(this.menu); 

        const createMenuHandler = () => {
            const closeOnOutsideClick = (e) => {
                if (e.target.contains(this.#innerMenu) || this.#innerMenu.contains(e.target)) return;

                this.#handler.close();
            };

            return {
                /**
                 * Check if inner menu is hidden.
                 * @returns {boolean}
                 */
                isHidden: () => {
                    return !this.#innerMenu.hasAttribute('open');
                },

                /**
                 * Open the inner menu.
                 * @returns {void}
                 */
                open: () => {
                    document.body.addEventListener('click', closeOnOutsideClick);
                    return this.#innerMenu.setAttribute('open', '');
                },

                /**
                 * Close the inner menu.
                 * @returns {void}
                 */
                close: () => {
                    document.body.removeEventListener('click', closeOnOutsideClick);
                    return this.#innerMenu.removeAttribute('open');
                },
            };
        };
        this.#handler = createMenuHandler();

        this.setPosition({x: this.storedPosition.x, y: this.storedPosition.y});
        this.menu.addEventListener('mousedown', this.#onDragStart);
        this.menu.addEventListener('touchstart', this.#onDragStart, { passive: false });
    }

    /**
     * Set the position of the inner menu based on current position.
     * @param {number} x - X coordinate, percentual. Clamped between 1~99.
     * @param {number} y - Y coordinate, percentual. Clamped between 1~99.
     */
    setInnerMenuPosition = (x, y) => {
        if (x > 50) {
            this.#innerMenu.style.right = this.#innerMenu.getBoundingClientRect().width + 'px';
        } else {
            this.#innerMenu.style.right = (-1 * this.menu.getBoundingClientRect().width) + 'px';
        }

        if (y > 50) {
            this.#innerMenu.style.top = (-1 * this.#innerMenu.getBoundingClientRect().height) + 'px';
        } else {
            this.#innerMenu.style.top = this.menu.getBoundingClientRect().height + 'px';
        }
    }

    /**
     * Set the position of the menu on screen.
     * @param {number} x - X coordinate. Clamped between 1~99.
     * @param {number} y - Y coordinate. Clamped between 1~99.
     * @returns {this} Current instance for chaining.
     */
    setPosition = ({x, y}) => {
        if (this.#innerMenu) {
            this.setInnerMenuPosition(x, y);
        }
        this.menu.style.left = `${x}%`;
        this.menu.style.top = `${y}%`;
        this.menu.style.right = 'auto';
        this.menu.style.bottom = 'auto';

        return this;
    }

    /**
     * Get the menu element.
     * @returns {HTMLElement} The outer menu element.
     */
    getMenu() {
        return this.menu;
    }

    /**
     * Get the handler object for the menu.
     * @returns {Object} The handler object.
     */
    getHandler () {
        return this.#handler;
    }

    /**
     * Get the inner menu element.
     * @returns {HTMLElement} The inner menu.
     */
    getInnerMenu() {
        return this.#innerMenu;
    }

    /**
     * Set the callback for click events.
     * @param {Function} callback - The callback function.
     */
    setOnClick = (callback) => {
        this.#onClick = callback;
    }

    /**
     * Set the callback for move events.
     * @param {Function} callback - The callback function.
     */
    setOnMove = (callback) => {
        this.#onMove = callback;
    }
}
